import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { useTableSort, type SortDir } from "@/components/ui/TableSort";
import { PageHeader, SectionCard } from "@/features/power_projects/components/ui";
import type { TechCalcProject } from "../types";
import { fmtDate } from "../utils";
import { useDeleteTechCalcProject, useTechCalcProjects } from "../hooks";
import { ProjectFormDialog } from "../components/ProjectFormDialog";

type SortField = "name" | "description" | "author" | "variants" | "created_at";

const authorOf = (p: TechCalcProject) => p.created_by_name?.trim() || "";

/** Klikany nagłówek kolumny — styl tabeli (.section-label) + wskaźnik kierunku. */
function SortHeader({
  field,
  label,
  sortField,
  sortDir,
  onSort,
  align = "left",
}: {
  field: SortField;
  label: string;
  sortField: SortField | null;
  sortDir: SortDir;
  onSort: (f: SortField) => void;
  align?: "left" | "right";
}) {
  const active = sortField === field;
  return (
    <th
      onClick={() => onSort(field)}
      title={`Sortuj wg: ${label}`}
      className={`section-label cursor-pointer select-none whitespace-nowrap px-4 py-2.5 transition-colors hover:text-[var(--color-text-primary)] ${
        align === "right" ? "text-right" : "text-left"
      }`}
    >
      {label}{" "}
      <span className={active ? "text-[var(--color-primary)]" : "opacity-30"}>
        {active ? (sortDir === "asc" ? "\u2191" : "\u2193") : "\u2195"}
      </span>
    </th>
  );
}

/** Lista projektów obliczeń technicznych (DLC_10) + tworzenie/edycja/usuwanie. */
export function TechCalcProjectsPage() {
  const navigate = useNavigate();
  const { data: projects, isLoading } = useTechCalcProjects();
  const deleteProject = useDeleteTechCalcProject();
  const [dialog, setDialog] = useState<{ open: boolean; project: TechCalcProject | null }>({
    open: false,
    project: null,
  });

  const { sortField, sortDir, handleSort } = useTableSort<SortField>();

  const rows = useMemo(() => {
    const list = projects ?? [];
    if (!sortField) return list;
    const dir = sortDir === "asc" ? 1 : -1;
    const txt = (a: string, b: string) => a.localeCompare(b, "pl", { sensitivity: "base" });
    // Empty cells ("—") always sink to the bottom, whichever direction is active.
    const blankLast = (a: string, b: string) => (a ? (b ? 0 : -1) : b ? 1 : 0);
    return [...list].sort((a, b) => {
      switch (sortField) {
        case "name":
          return dir * txt(a.name, b.name);
        case "description": {
          const [da, db] = [a.description ?? "", b.description ?? ""];
          return blankLast(da, db) || dir * txt(da, db);
        }
        case "author": {
          const [aa, ab] = [authorOf(a), authorOf(b)];
          return blankLast(aa, ab) || dir * txt(aa, ab);
        }
        case "variants":
          return dir * ((a.variants_count ?? 0) - (b.variants_count ?? 0));
        case "created_at":
          return dir * (Date.parse(a.created_at) - Date.parse(b.created_at));
        default:
          return 0;
      }
    });
  }, [projects, sortField, sortDir]);

  return (
    <div>
      <PageHeader
        title="Obliczenia techniczne"
        subtitle="Obliczenia sił na słupach linii nN — projekty i warianty"
        actions={
          <Button size="sm" onClick={() => setDialog({ open: true, project: null })}>
            <Plus size={14} className="mr-1" /> Nowy projekt
          </Button>
        }
      />

      <SectionCard bodyClassName="p-0">
        {isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : rows.length === 0 ? (
          <p className="py-10 text-center font-mono text-xs text-[var(--color-text-muted)]">
            Brak projektów — utwórz pierwszy przyciskiem „Nowy projekt".
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] text-left">
                  {(
                    [
                      { field: "name", label: "Nazwa" },
                      { field: "description", label: "Opis" },
                      { field: "author", label: "Autor" },
                      { field: "variants", label: "Warianty", align: "right" },
                      { field: "created_at", label: "Utworzono", align: "right" },
                    ] as { field: SortField; label: string; align?: "left" | "right" }[]
                  ).map((c) => (
                    <SortHeader
                      key={c.field}
                      field={c.field}
                      label={c.label}
                      align={c.align}
                      sortField={sortField}
                      sortDir={sortDir}
                      onSort={handleSort}
                    />
                  ))}
                  <th className="w-20 px-4 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr
                    key={p.id}
                    onClick={() => navigate(`/tech-calc/${p.id}`)}
                    className="cursor-pointer border-b border-[var(--color-border-muted)] transition-colors last:border-0 hover:bg-[var(--color-bg-elevated)]"
                  >
                    <td className="px-4 py-2.5 font-medium text-[var(--color-text-primary)]">
                      {p.name}
                    </td>
                    <td className="max-w-[420px] truncate px-4 py-2.5 text-[var(--color-text-secondary)]">
                      {p.description || "—"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-[var(--color-text-secondary)]">
                      {authorOf(p) || "—"}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs">
                      {p.variants_count ?? "—"}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs text-[var(--color-text-secondary)]">
                      {fmtDate(p.created_at)}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          title="Edytuj projekt"
                          onClick={() => setDialog({ open: true, project: p })}
                          className="p-1 text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-text-primary)]"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          type="button"
                          title="Usuń projekt"
                          onClick={() => {
                            if (
                              window.confirm(
                                `Usunąć projekt „${p.name}" wraz z wariantami i wynikami?`,
                              )
                            )
                              deleteProject.mutate(p.id);
                          }}
                          className="p-1 text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-primary)]"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <ProjectFormDialog
        open={dialog.open}
        project={dialog.project}
        onClose={() => setDialog({ open: false, project: null })}
        onSaved={(id, created) => {
          if (created) navigate(`/tech-calc/${id}`);
        }}
      />
    </div>
  );
}
